// src/utils/validateMatchData.js
import { getLeagueConfig } from "./leagueConfig";
import { OWN_GOAL_ID } from "./matchConstants";

export default function validateMatchData(matchDoc, leagueType) {
  const leagueConfig = getLeagueConfig(leagueType) || {};
  const {
    appearanceData = {},            // { [playerId]: { appeared: boolean, isGK?: boolean } }
    starters = {},               // { [playerId]: true }  OR array of ids
    goals = [],                  // [{ scorer, assist? }]
    cleanSheet,                  // boolean
    bestPlayers = {},            // { 1: pid, 2: pid, 3: pid }
    infractionAssignments = {},  // { "Yellow Card": [pid, ...], "Penalty Save": pid, ... }
  } = matchDoc;

  const errors = [];

  // Normalize
  const starterIds = Array.isArray(starters)
    ? starters
    : Object.keys(starters || {}).filter((id) => !!starters[id]);

  const appearedIds = Object.keys(appearanceData || {}).filter(
    (id) => appearanceData[id]?.appeared === true
  );

  // --- Appearances
  if (appearedIds.length === 0) {
    errors.push("At least one player must be marked as having appeared.");
  }

  // --- Starters subset of appearances
  if (starterIds.length === 0) {
    errors.push("At least one starter must be selected.");
  }
  const invalidStarters = starterIds.filter((id) => !appearedIds.includes(id));
  if (invalidStarters.length > 0) {
    errors.push("One or more starters were not marked as having appeared.");
  }

  // --- League config driven validations

  // Validate starters count against positionsRequired if defined
  if (leagueConfig.positionsRequired) {
    const totalRequiredStarters = Object.values(leagueConfig.positionsRequired).reduce((sum, val) => sum + val, 0);
    if (starterIds.length !== totalRequiredStarters) {
      errors.push(`Number of starters must be exactly ${totalRequiredStarters} according to league configuration.`);
    }
  }

  // Validate appearances count against squadSize if defined
  if (typeof leagueConfig.squadSize === "number") {
    if (appearedIds.length > leagueConfig.squadSize) {
      errors.push(`Number of players appeared cannot exceed squad size of ${leagueConfig.squadSize}.`);
    }
  }

  const bonus = [bestPlayers?.[1], bestPlayers?.[2], bestPlayers?.[3]].filter(Boolean);

  // Require bonus players to be unique (only if there are duplicates)
  if (bonus.length > 0 && new Set(bonus).size !== bonus.length) {
    errors.push(`Bonus players must be unique.`);
  }

  // Validate bonus players appeared
  for (const pid of bonus) {
    if ((pid in appearanceData) && !appearanceData[pid].appeared) {
      errors.push(`Bonus player ${pid} was not marked as having appeared.`);
    }
  }

  // --- Goals
  goals.forEach((g, i) => {
    if (!g?.scorer) errors.push(`Goal #${i + 1} is missing a scorer.`);
    if (g?.assist && g.assist === g.scorer) {
      errors.push(`Goal #${i + 1} has the same player for scorer and assist.`);
    }
    if (g?.assist === OWN_GOAL_ID) {
      errors.push(`Goal #${i + 1} has an invalid assist.`);
    }
  });

  // --- Clean sheet must be boolean
  if (typeof cleanSheet !== "boolean") {
    errors.push("Clean sheet must be marked as true or false.");
  }

  // --- Infractions: validate assignments (not the selected list)
  for (const [infraction, value] of Object.entries(infractionAssignments || {})) {
    const assigned = Array.isArray(value) ? value : (value ? [value] : []);
    if (assigned.length === 0) {
      errors.push(`Infraction "${infraction}" was selected but no player was assigned.`);
      continue;
    }
    for (const pid of assigned) {
      if ((pid in appearanceData) && !appearanceData[pid].appeared) {
        errors.push(`Infraction assigned to non-appearing player: ${infraction}`);
        break;
      }
    }
  }

  return errors;
}